'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { trackEvent } from './posthog';
import { EVENTS } from './events';
import { detectAiProvider } from './referrer-parser';

const AI_COOKIE = 'agora_ai_referral';

function persistAiProvider(provider) {
  const domain = window.location.hostname.endsWith('agora.red') ? '; domain=.agora.red' : '';
  document.cookie = `${AI_COOKIE}=${encodeURIComponent(provider)}; path=/; max-age=${60 * 60 * 24 * 30}${domain}; samesite=lax`;
}

function getPageType(pathname) {
  if (pathname === '/') return 'home';
  if (pathname === '/negocios') return 'marketplace';
  if (pathname.startsWith('/negocios/')) return 'vendor_profile';
  if (pathname.startsWith('/directorio')) return 'directory';
  if (pathname === '/signin' || pathname === '/signup' || pathname === '/callback') return 'auth';
  if (pathname === '/onboarding') return 'onboarding';
  if (pathname.startsWith('/perfil')) return 'profile';
  // Single-segment paths are category pages
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 1 && segments.length <= 3) return 'category';
  return 'other';
}

function getPageName(pathname) {
  if (pathname === '/') return 'Home';
  if (pathname === '/negocios') return 'Marketplace';
  if (pathname.startsWith('/negocios/')) return 'Vendor Profile';
  if (pathname === '/directorio') return 'Directory';
  if (pathname.startsWith('/directorio/provincia/')) return 'Directory Province';
  if (pathname.startsWith('/directorio/')) return 'Directory Business';
  if (pathname === '/signin') return 'Sign In';
  if (pathname === '/signup') return 'Sign Up';
  if (pathname === '/onboarding') return 'Onboarding';
  if (pathname.startsWith('/perfil')) return 'Profile';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 1) return `Category: ${segments[0]}`;
  if (segments.length === 2) return `Category: ${segments[0]}/${segments[1]}`;
  if (segments.length === 3) return `Category: ${segments[0]}/${segments[1]}/${segments[2]}`;
  return pathname;
}

export function usePageTracking() {
  const pathname = usePathname();
  const previousPathname = useRef(null);

  useEffect(() => {
    if (pathname === previousPathname.current) return;
    previousPathname.current = pathname;

    trackEvent(EVENTS.PAGE_VIEWED, {
      page_name: getPageName(pathname),
      page_path: pathname,
      page_type: getPageType(pathname),
      referrer: typeof document !== 'undefined' ? document.referrer : undefined
    });

    const params = new URLSearchParams(window.location.search);
    const aiProvider = detectAiProvider(params.get('utm_source'), document.referrer);
    if (aiProvider) {
      persistAiProvider(aiProvider);
      const aiProps = { ai_provider: aiProvider, page_path: pathname };
      trackEvent(EVENTS.AI_REFERRAL_LANDING, aiProps);
      if (pathname.startsWith('/negocios/')) {
        trackEvent(EVENTS.AI_PROVIDER_VIEW, aiProps);
      }
    }
  }, [pathname]);
}
